import config from '@/core/config/config';
import { SLOTS } from '@/graphql/queries';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { SlotsItem, SlotsOptions } from '@/types';
import { customFetcher, getGqlString } from '@/utils/customFetcher';
import { isResearchOnlyLocation } from '@/utils/replaceLocationBasedTerms';
import { getDateToQuerySlots } from '@/utils/sku370';
import { getAvailableSlotsForDate } from '@/utils/slotCalendarFunctions';
import { mutationConfig, swrConfig } from '@/utils/swrConfig';
import { format, getDaysInMonth, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useRef } from 'react';
import useSWRMutation from 'swr/mutation';
import { useShallow } from 'zustand/react/shallow';

export const useSlots = () => {
  const {
    locationId,
    locationTimezone,
    skuId,
    slots,
    setSlots,
    setClosestAvailableDate,
    setIsSlotsLoading,
    setDate,
    isSlotsLoading,
  } = usePrenuvoStore(
    useShallow((state) => ({
      locationId: state.scan.location?.code,
      locationTimezone: state.scan.location?.timezone,
      skuId: state.scan.sku?.skuID,
      slots: state.scan.slots,
      setSlots: state.scan.methods.setSlots,
      setClosestAvailableDate: state.scan.methods.setClosestAvailableDate,
      setDate: state.scan.methods.setDate,
      setIsSlotsLoading: state.ui.methods.setIsSlotsLoading,
      isSlotsLoading: state.ui.isSlotsLoading,
    })),
  );

  // TODO: Refactor to use useAbortController hook
  const abortController = useRef<AbortController | null>(null);

  const abortPreviousRequest = () => {
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();
  };

  const triggerSlotsMutationFetcher = (url: string, { arg }: { arg: any }) => {
    return customFetcher(url, {
      query: getGqlString(SLOTS),
      variables: {
        filter: arg,
      },
      signal: abortController.current?.signal,
    });
  };

  const useTriggerSlots = () => {
    return useSWRMutation(config.APPS_SERVER, triggerSlotsMutationFetcher, mutationConfig);
  };
  const { trigger: triggerSlots, error: slotsError, isMutating } = useTriggerSlots();

  useEffect(() => {
    setIsSlotsLoading(isMutating);
  }, [isMutating, setIsSlotsLoading]);

  const inInSlotsRange = useCallback(
    (slotDate: Date) => slots.some((slot) => slot.date === format(slotDate, 'yyyy-MM-dd')),
    [slots],
  );

  const slotsQueryPayload = useCallback(
    (data: Date | undefined, options: SlotsOptions) => {
      const locationCode = options?.locationId || (locationId as string);
      return {
        // getting the first day of the month and adding 1 to it
        // due to the API returning the last day of the previous month
        days: getDaysInMonth(data || new Date()) + 1,
        // date: options?.date || (data ? format(data, 'yyyy-MM-dd') : undefined),
        date: getDateToQuerySlots(options, data, Number(skuId), locationCode),
        locationId: locationCode,
        skuId: Number(options?.skuId || skuId),
        onlineBookingType: isResearchOnlyLocation(locationCode)
          ? 'RESEARCH_BOOKING'
          : 'ONLINE_BOOKING',
      };
    },
    [locationId, skuId],
  );

  const getSlots = useCallback(
    async (date: Date | undefined = undefined, options: SlotsOptions = undefined) => {
      // Note: Line below could be useful for debugging
      // options?.callerContext && console.info('🐞 Debugging slots fetch: ', options?.callerContext);

      if (!locationId || !skuId) return;
      if (date && !options?.disableCache && inInSlotsRange(date)) return;

      abortPreviousRequest();

      const response: any = await triggerSlots(slotsQueryPayload(date, options));
      const slots: SlotsItem[] = response?.data?.slots;

      if (!(slots?.length > 0)) return;

      setSlots(slots);
      const firstSlotWithAvailability =
        slots.find((slot) => slot.availableSlots?.length > 0) || slots[0];

      if (date) {
        const { isAvailable } = getAvailableSlotsForDate(slots, date);
        if (isAvailable) return;
        // If the selected date has no available slots, set the date to the first available slot
        setDate(parseISO(firstSlotWithAvailability.date));
        return;
      }

      setClosestAvailableDate(firstSlotWithAvailability.date);
      const availableSlotTimestamp = firstSlotWithAvailability.availableSlots[0].utcStart * 1000;
      const availableSlotDate = new Date(availableSlotTimestamp);
      if (locationTimezone) {
        const zonedDate = toZonedTime(availableSlotDate, locationTimezone);
        setDate(zonedDate);
      }
    },
    [
      inInSlotsRange,
      isMutating,
      locationId,
      setClosestAvailableDate,
      setDate,
      setSlots,
      skuId,
      slotsQueryPayload,
      triggerSlots,
      locationTimezone,
    ],
  );

  return { getSlots, isSlotsLoading, slotsError };
};
